import React, { useState, useEffect, useRef } from 'react';
import RaidCard from './RaidCard';
import './RaidFinder.css';

const RaidFinder = ({ websocket, connectionStatus }) => {
  const [raids, setRaids] = useState([]);
  const [currentView, setCurrentView] = useState(0);
  const [raidsPerView] = useState(3);
  const batchRaidsRef = useRef([]);
  const batchTimeoutRef = useRef(null);

  useEffect(() => {
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      // Subscribe to raid finder updates
      const subscribeMessage = {
        action: 'subscribeRaidFinder'
      };
      websocket.send(JSON.stringify(subscribeMessage));
    }
  }, [websocket]);

  // Handle WebSocket messages for raid data
  useEffect(() => {
    if (websocket) {
      const handleMessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Check for direct raidUpdate or nested raidUpdate in newMessage
          if (data.type === 'raidUpdate' || (data.type === 'newMessage' && data.message && data.message.type === 'raidUpdate')) {
            const raidData = data.type === 'raidUpdate' ? data : data.message;
            
            // Clear any existing timeout
            if (batchTimeoutRef.current) {
              clearTimeout(batchTimeoutRef.current);
            }
            
            // Add this raid to the batch
            batchRaidsRef.current.push(raidData.raid);
            
            // Set a timeout to process the complete batch
            batchTimeoutRef.current = setTimeout(() => {
              // Sort and set all raids at once (this replaces the entire list)
              const sortedRaids = [...batchRaidsRef.current].sort((a, b) => a.id - b.id);
              setRaids(sortedRaids);
              
              // Clear the batch for next time
              batchRaidsRef.current = [];
            }, 200); // 200ms delay to collect all raids in the batch
          }
        } catch (error) {
        }
      };

      websocket.addEventListener('message', handleMessage);
      
      return () => {
        websocket.removeEventListener('message', handleMessage);
      };
    }
  }, [websocket]);

  // Calculate pagination
  const totalViews = Math.ceil(raids.length / raidsPerView);
  const startIndex = currentView * raidsPerView;
  const endIndex = startIndex + raidsPerView;
  const currentRaids = raids.slice(startIndex, endIndex);

  const handlePreviousView = () => {
    setCurrentView(prev => Math.max(0, prev - 1));
  };

  const handleNextView = () => {
    setCurrentView(prev => Math.min(totalViews - 1, prev + 1));
  };

  return (
    <div className="raid-finder">
      <div className="raid-finder-background">
        {/* View Controls */}
        {raids.length > raidsPerView && (
          <div className="raid-view-controls">
            <button 
              onClick={handlePreviousView}
              disabled={currentView === 0}
              className="view-nav-btn prev-btn"
            >
              ← Prev
            </button>
            <span className="view-indicator">
              View {currentView + 1} of {totalViews}
            </span>
            <button 
              onClick={handleNextView}
              disabled={currentView === totalViews - 1}
              className="view-nav-btn next-btn"
            >
              Next →
            </button>
          </div>
        )}
        
        <div className="raid-list">
          {raids.length === 0 ? (
            <div style={{ color: '#fff', padding: '20px', textAlign: 'center' }}>
              No raids found. Waiting for raid data...
            </div>
          ) : (
            currentRaids.map((raid) => (
              <RaidCard key={raid.id} raid={raid} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RaidFinder;
